import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Typography,
  TextField,
  Button,
  Alert,
  Box,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
} from '@mui/material';
import {
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  ViewList as BatchIcon,
  ViewModule as SingleIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { Unit, Lease } from '@urbanmix-tech/shared-js';
import { saveLease } from '@/data';
import { getLongNumberStr, convertPseudoDateToRealDate } from '@/helpers/common';
import { calculateLeaseLength, calculateNewLeaseDatesFromLease } from '@/helpers/leaseHelpers';
import { useDispatch } from 'react-redux';
import {
  setBuildingVisual,
  setHighlightedUnits,
  setSelectedUnit,
} from '@/features/project/projectSlice';
import { ColorGradeVisualizer } from '@urbanmix-tech/um-embedded';
import { colorScales } from '@/components/BuildingTabs/BuildingVisualizers';
import { ComparablesSection } from '@/components/BuildingTabs/PricingTool/ComparablesSection';
import { calculateEffectivePPSF } from '@/components/BuildingTabs/PricingTool/comparablesUtils';
import { withLegendConfig, commonLegendConfigs } from '@/components/BuildingModel/visualizerConfig';
import { PricingWizardContextValue, DEFAULT_PRICING_INPUT } from '../types';

interface PricingSingleModeProps {
  units: Unit[];
  selectedUnits: Unit[];
  leases: Lease[];
  allLeases: Lease[];
  leaseMap: Map<string, Lease>;
  wizard: PricingWizardContextValue;
}

export function PricingSingleMode({
  units,
  selectedUnits,
  leases,
  allLeases,
  leaseMap,
  wizard,
}: PricingSingleModeProps) {
  const dispatch = useDispatch();
  const { state, actions, currentUnit, pricedUnitsCount, totalSelectedCount } = wizard;
  const { currentSingleUnitIndex, unitPricingData } = state;

  const [showConcessionsAsMultiples, setShowConcessionsAsMultiples] = useState(true);
  const [creationMessage, setCreationMessage] = useState<string | null>(null);

  // Get current unit's pricing data
  const currentPricing = currentUnit
    ? unitPricingData.get(currentUnit.id) || {
        ...DEFAULT_PRICING_INPUT,
        unitId: currentUnit.id,
      }
    : null;

  // Local state for form inputs (controlled components) - use string | number to allow empty values
  const [rentIncreasePercent, setRentIncreasePercent] = useState<string | number>(
    currentPricing?.rentIncreasePercent ?? 0
  );
  const [concessionMonths, setConcessionMonths] = useState<string | number>(
    currentPricing?.concessionMonths ?? 0
  );
  const [leaseLengthMonths, setLeaseLengthMonths] = useState<string | number>(
    currentPricing?.leaseLengthMonths ?? 12
  );

  // Helper to get numeric value (treats empty string as 0)
  const getNumericValue = (value: string | number, defaultValue = 0): number => {
    if (value === '' || value === null || value === undefined) return defaultValue;
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(num) ? defaultValue : num;
  };

  // Reset form when unit changes
  useEffect(() => {
    if (currentUnit) {
      const pricing = unitPricingData.get(currentUnit.id);
      setRentIncreasePercent(pricing?.rentIncreasePercent ?? 0);
      setConcessionMonths(pricing?.concessionMonths ?? 0);
      setLeaseLengthMonths(pricing?.leaseLengthMonths ?? 12);
    }
  }, [currentUnit?.id, unitPricingData]);

  // Get current lease for the unit
  const currentLease = currentUnit ? leaseMap.get(currentUnit.id) : null;

  // Helper to safely convert lease dates
  const getLeaseEndDate = (lease: Lease): Date | null => {
    return convertPseudoDateToRealDate(lease.leaseEndDate as any);
  };

  const getLeaseStartDate = (lease: Lease): Date | null => {
    return convertPseudoDateToRealDate(lease.leaseStartDate as any);
  };

  // Calculate current lease metrics
  const currentMetrics = useMemo(() => {
    if (!currentLease || !currentUnit) return null;

    const startDate = getLeaseStartDate(currentLease);
    const endDate = getLeaseEndDate(currentLease);
    if (!startDate || !endDate) return null;

    const leaseLength = calculateLeaseLength(currentLease);
    const sqft = currentUnit.sqft || 1;

    const totalConcessions =
      Math.round(
        (Array.isArray(currentLease.concessions)
          ? currentLease.concessions.reduce(
              (sum: number, c: any) =>
                sum + (c.amount || 0) * currentLease.monthlyRent,
              0
            )
          : ((currentLease.concessions as number) || 0) * currentLease.monthlyRent) * 100
      ) / 100;

    const effectiveMonthlyRent =
      leaseLength > 0
        ? Math.round((currentLease.monthlyRent - totalConcessions / leaseLength) * 100) / 100
        : currentLease.monthlyRent;
    const effectiveAnnualRent = effectiveMonthlyRent * 12;
    const effectiveAnnualRentPerSqft =
      Math.round((effectiveAnnualRent / sqft) * 100) / 100;
    const annualRent = currentLease.monthlyRent * 12;
    const annualRentPerSqft = annualRent / sqft;

    return {
      monthlyRent: currentLease.monthlyRent,
      leaseLength,
      concessions: totalConcessions,
      effectiveMonthlyRent,
      effectiveAnnualRent,
      effectiveAnnualRentPerSqft,
      annualRent,
      annualRentPerSqft,
      sqft,
      leaseStartDate: startDate,
      leaseEndDate: endDate,
    };
  }, [currentLease, currentUnit]);

  // Calculate proposed metrics
  const proposedMetrics = useMemo(() => {
    if (!currentMetrics) return null;

    const increaseNum = getNumericValue(rentIncreasePercent);
    const concessionNum = getNumericValue(concessionMonths);
    const lengthNum = getNumericValue(leaseLengthMonths, 12);

    const proposedMonthlyRent =
      currentMetrics.monthlyRent * (1 + increaseNum / 100);
    const proposedConcessionAmount = concessionNum * proposedMonthlyRent;
    const proposedEffectiveMonthlyRent =
      proposedMonthlyRent - proposedConcessionAmount / lengthNum;
    const proposedEffectiveAnnualRent = proposedEffectiveMonthlyRent * 12;
    const proposedEffectiveAnnualRentPerSqft =
      Math.round((proposedEffectiveAnnualRent / currentMetrics.sqft) * 100) / 100;
    const proposedAnnualRent = proposedMonthlyRent * 12;
    const proposedAnnualRentPerSqft = proposedAnnualRent / currentMetrics.sqft;

    const netIncreaseAmount =
      proposedEffectiveMonthlyRent - currentMetrics.effectiveMonthlyRent;
    const netIncreasePercent =
      (netIncreaseAmount / currentMetrics.effectiveMonthlyRent) * 100;

    return {
      monthlyRent: Math.round(proposedMonthlyRent * 100) / 100,
      leaseLength: lengthNum,
      concessions: Math.round(proposedConcessionAmount * 100) / 100,
      effectiveMonthlyRent: Math.round(proposedEffectiveMonthlyRent * 100) / 100,
      effectiveAnnualRent: Math.round(proposedEffectiveAnnualRent * 100) / 100,
      effectiveAnnualRentPerSqft: proposedEffectiveAnnualRentPerSqft,
      annualRent: Math.round(proposedAnnualRent * 100) / 100,
      annualRentPerSqft: Math.round(proposedAnnualRentPerSqft * 100) / 100,
      netIncreaseAmount: Math.round(netIncreaseAmount * 100) / 100,
      netIncreasePercent: Math.round(netIncreasePercent * 100) / 100,
    };
  }, [currentMetrics, rentIncreasePercent, concessionMonths, leaseLengthMonths]);

  // Calculate proposed lease dates
  const proposedDates = useMemo(() => {
    if (!currentLease || !proposedMetrics) return undefined;
    try {
      return calculateNewLeaseDatesFromLease(currentLease, { months: proposedMetrics.leaseLength });
    } catch {
      return undefined;
    }
  }, [currentLease, proposedMetrics?.leaseLength]);

  // Update building visualization
  useEffect(() => {
    if (!currentUnit || !proposedMetrics) {
      dispatch(
        setBuildingVisual({
          apartmentData: [],
          visualizer: null,
        })
      );
      return;
    }

    // Build visualization data for comparable units
    const comparableUnits = units
      .filter((u) => u.id !== currentUnit.id && leaseMap.has(u.id))
      .filter(
        (u) =>
          u.numBedrooms === currentUnit.numBedrooms || u.line === currentUnit.line
      );

    const values = [
      proposedMetrics.effectiveAnnualRentPerSqft,
      ...comparableUnits
        .map((u) => {
          const lease = leaseMap.get(u.id);
          if (!lease) return 0;
          return calculateEffectivePPSF(u, lease);
        })
        .filter((v) => v > 0),
    ];

    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const visualizer = new ColorGradeVisualizer(
      colorScales.default,
      minValue,
      maxValue
    );

    const apartmentData = [
      {
        apartmentId: currentUnit.id,
        value: proposedMetrics.effectiveAnnualRentPerSqft,
        label: 'Proposed Effective PPSF',
      },
      ...comparableUnits
        .map((u) => {
          const lease = leaseMap.get(u.id);
          if (!lease) return null;
          return {
            apartmentId: u.id,
            value: calculateEffectivePPSF(u, lease),
            label: 'Comparable PPSF',
          };
        })
        .filter(Boolean) as any[],
    ];

    dispatch(
      setBuildingVisual({
        apartmentData,
        visualizer,
      })
    );

    dispatch(setSelectedUnit({ apartmentId: currentUnit.id }));
  }, [currentUnit, proposedMetrics, units, leaseMap, dispatch]);

  // Handle creating draft lease
  const handleCreateDraftLease = async () => {
    if (!currentUnit || !currentLease || !proposedMetrics) return;

    const proposedConcessionsArray: any[] = [];
    if (proposedMetrics.concessions > 0) {
      const concMonths = proposedMetrics.concessions / proposedMetrics.monthlyRent;
      proposedConcessionsArray.push({
        month: 'draft',
        year: 'draft',
        amount: concMonths,
        type: 'rentMultiplier',
      });
    }

    try {
      const { startDate, endDate } = calculateNewLeaseDatesFromLease(
        currentLease, 
        { months: proposedMetrics.leaseLength }
      );
      
      // Reuse existing draft lease ID if updating, otherwise create new one
      const existingDraftId = currentPricing?.draftLeaseId;
      const draftLease: any = {
        ...currentLease,
        id: existingDraftId || `draft_${currentUnit.id}_${Date.now()}`,
        status: 'draft',
        monthlyRent: proposedMetrics.monthlyRent,
        leaseStartDate: startDate,
        leaseEndDate: endDate,
        concessions: proposedConcessionsArray,
        totalConcessionAmount: proposedMetrics.concessions,
      };

      await saveLease(draftLease);

      // Update wizard state
      actions.markUnitAsPriced(currentUnit.id, draftLease.id);
      actions.updateUnitPricing(currentUnit.id, {
        rentIncreasePercent: getNumericValue(rentIncreasePercent),
        concessionMonths: getNumericValue(concessionMonths),
        leaseLengthMonths: getNumericValue(leaseLengthMonths, 12),
        isPriced: true,
        draftLeaseId: draftLease.id,
      });

      const actionVerb = existingDraftId ? 'updated' : 'created';
      setCreationMessage(
        `Draft lease ${actionVerb} for ${currentUnit.name || currentUnit.id}`
      );
      setTimeout(() => setCreationMessage(null), 3000);

      // Auto-advance to next unit if not the last one (only on first creation)
      if (!existingDraftId && currentSingleUnitIndex < selectedUnits.length - 1) {
        setTimeout(() => {
          actions.setCurrentSingleUnitIndex(currentSingleUnitIndex + 1);
        }, 500);
      }
    } catch (error) {
      console.error('Error saving draft lease:', error);
      setCreationMessage('Error saving draft lease');
      setTimeout(() => setCreationMessage(null), 3000);
    }
  };

  // Navigation handlers
  const handlePrevUnit = () => {
    if (currentSingleUnitIndex > 0) {
      actions.setCurrentSingleUnitIndex(currentSingleUnitIndex - 1);
    }
  };

  const handleNextUnit = () => {
    if (currentSingleUnitIndex < selectedUnits.length - 1) {
      actions.setCurrentSingleUnitIndex(currentSingleUnitIndex + 1);
    }
  };

  // Handle visualization group change from ComparablesSection
  const handleVisualizationGroupChange = useCallback(
    (unitIds: string[], groupName: string) => {
      if (!proposedMetrics || !currentUnit) return;

      const apartmentData = unitIds
        .map((id) => {
          const u = units.find((unit) => unit.id === id);
          if (!u) return null;

          const lease = leaseMap.get(u.id);
          if (!lease) return null;

          return {
            apartmentId: u.id,
            value: calculateEffectivePPSF(u, lease),
            label: 'Effective PPSF',
          };
        })
        .filter(Boolean) as any[];

      // Add proposed unit at the beginning
      apartmentData.unshift({
        apartmentId: currentUnit.id,
        value: proposedMetrics.effectiveAnnualRentPerSqft,
        label: 'Proposed Effective PPSF',
      });

      const values = apartmentData.map((d) => d.value);
      if (values.length > 0) {
        const minValue = Math.min(...values);
        const maxValue = Math.max(...values);
        const visualizer = new ColorGradeVisualizer(
          colorScales.default,
          minValue,
          maxValue
        );
        withLegendConfig(visualizer, commonLegendConfigs.ppsqf);

        dispatch(
          setBuildingVisual({
            apartmentData,
            visualizer,
          })
        );

        setTimeout(() => {
          dispatch(
            setHighlightedUnits(apartmentData.filter((ad) => ad.value > 0))
          );
        }, 100);
      }
    },
    [units, leaseMap, proposedMetrics, currentUnit, dispatch]
  );

  if (!currentUnit) {
    return (
      <div className="flex items-center justify-center h-full">
        <Typography variant="body1" color="textSecondary">
          No units selected
        </Typography>
      </div>
    );
  }

  if (!currentLease) {
    return (
      <div className="flex items-center justify-center h-full">
        <Typography variant="body1" color="textSecondary">
          No active lease found for this unit
        </Typography>
      </div>
    );
  }

  return (
    <div className="pricing-single-mode h-full flex flex-col min-h-0">
      {/* Unit navigation */}
      <div className="flex-shrink-0 px-8 py-3 border-b border-zinc-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <IconButton
            onClick={handlePrevUnit}
            disabled={currentSingleUnitIndex === 0}
            size="small"
          >
            <PrevIcon />
          </IconButton>
          <Typography variant="body1">
            <strong>{currentUnit.name || currentUnit.id}</strong>
            <span className="text-zinc-400 ml-2">
              ({currentSingleUnitIndex + 1} of {selectedUnits.length})
            </span>
          </Typography>
          <IconButton
            onClick={handleNextUnit}
            disabled={currentSingleUnitIndex === selectedUnits.length - 1}
            size="small"
          >
            <NextIcon />
          </IconButton>
        </div>
        <div className="flex items-center gap-3">
          {/* Progress indicator */}
          <div className="flex items-center gap-2">
            <Typography variant="body2" className="text-zinc-400">
              <strong className="text-zinc-200">{pricedUnitsCount}</strong>/{totalSelectedCount} priced
            </Typography>
            <Box sx={{ width: 80 }}>
              <LinearProgress
                variant="determinate"
                value={totalSelectedCount > 0 ? (pricedUnitsCount / totalSelectedCount) * 100 : 0}
                sx={{ height: 8, borderRadius: 1 }}
              />
            </Box>
          </div>

          {/* Mode toggle */}
          <ToggleButtonGroup
            value={state.pricingMode}
            exclusive
            onChange={(_, value) => {
              if (value !== null) {
                actions.setPricingMode(value);
              }
            }}
            size="small"
            sx={{ flexShrink: 0 }}
          >
            <ToggleButton value="single" sx={{ px: 1.5, py: 0.5 }}>
              <SingleIcon sx={{ fontSize: 18, mr: 0.5 }} />
              Single
            </ToggleButton>
            <ToggleButton value="batch" sx={{ px: 1.5, py: 0.5 }}>
              <BatchIcon sx={{ fontSize: 18, mr: 0.5 }} />
              Batch
            </ToggleButton>
          </ToggleButtonGroup>

          {currentPricing?.isPriced && (
            <Typography variant="body2" color="success.main">
              Priced
            </Typography>
          )}
          <Typography variant="body2" color="textSecondary">
            {currentUnit.numBedrooms}BR, {currentUnit.sqft} sqft
          </Typography>
        </div>
      </div>

      {creationMessage && (
        <Alert
          severity={creationMessage.includes('Error') ? 'error' : 'success'}
          className="mx-8 mt-4"
        >
          {creationMessage}
        </Alert>
      )}

      {/* Proposed Terms Inputs */}
      <div className="flex-shrink-0 px-8 py-4">
        <div className="border border-zinc-600 rounded-md p-4">
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center gap-4">
              <Typography variant="subtitle2" className="font-bold">
                Proposed Terms
              </Typography>
              <ToggleButtonGroup
                value={showConcessionsAsMultiples ? 'months' : 'dollars'}
                exclusive
                onChange={(_, value) => {
                  if (value !== null) {
                    setShowConcessionsAsMultiples(value === 'months');
                  }
                }}
                size="small"
                sx={{ height: 28 }}
              >
                <ToggleButton value="months" sx={{ fontSize: '0.7rem', px: 1.5 }}>
                  Conc. (mo)
                </ToggleButton>
                <ToggleButton value="dollars" sx={{ fontSize: '0.7rem', px: 1.5 }}>
                  Conc. ($)
                </ToggleButton>
              </ToggleButtonGroup>
            </div>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleCreateDraftLease}
              disabled={!proposedMetrics}
            >
              {currentPricing?.isPriced ? 'Update Draft Lease' : 'Create Draft Lease'}
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Typography variant="body2" color="textSecondary" className="mb-1">
                Rent Increase (%)
              </Typography>
              <TextField
                type="number"
                value={rentIncreasePercent}
                onChange={(e) => setRentIncreasePercent(e.target.value)}
                inputProps={{ step: 0.1 }}
                fullWidth
                size="small"
                helperText={
                  currentMetrics
                    ? `Current: $${getLongNumberStr(
                        Math.round(currentMetrics.monthlyRent)
                      )} → Proposed: $${getLongNumberStr(
                        Math.round(proposedMetrics?.monthlyRent || 0)
                      )}`
                    : ''
                }
              />
            </div>
            <div>
              <Typography variant="body2" color="textSecondary" className="mb-1">
                Concessions (Months)
              </Typography>
              <TextField
                type="number"
                value={concessionMonths}
                onChange={(e) => setConcessionMonths(e.target.value)}
                inputProps={{ step: 0.5 }}
                fullWidth
                size="small"
              />
            </div>
            <div>
              <Typography variant="body2" color="textSecondary" className="mb-1">
                Lease Length (Months)
              </Typography>
              <TextField
                type="number"
                value={leaseLengthMonths}
                onChange={(e) => setLeaseLengthMonths(e.target.value)}
                fullWidth
                size="small"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Comparables Section */}
      {currentUnit && currentMetrics && proposedMetrics && (
        <div className="flex-1 min-h-0 px-8 pb-4 overflow-auto">
          <div className="border border-zinc-600 rounded-md overflow-hidden">
            <ComparablesSection
              selectedUnit={currentUnit}
              allUnits={units}
              leaseMap={leaseMap}
              allLeases={allLeases}
              proposedMetrics={proposedMetrics}
              currentMetrics={currentMetrics}
              proposedDates={proposedDates}
              showConcessionsAsMultiples={showConcessionsAsMultiples}
              onVisualizationGroupChange={handleVisualizationGroupChange}
            />
          </div>
        </div>
      )}
    </div>
  );
}
